import React, { useState, useEffect } from "react";
import { Bell, CheckCircle, Check, X, Search, Trash2, Mail, MailOpen } from "lucide-react";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "../components/ui/LoadingComponent";
import { toast } from "react-toastify";
import Breadcrumb from "../components/Breadcrumb";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  // Fetch notifications from API
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Filter notifications when filter or search changes
  useEffect(() => {
    filterNotifications();
  }, [notifications, filter, searchTerm]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiCall("get", "/get-my-notifications");
      if (response && response.data) {
        const notifData = response.data.data || response.data;
        setNotifications(notifData);
        setFilteredNotifications(notifData);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to load notifications");
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const filterNotifications = () => {
    let filtered = [...notifications];

    if (filter === "unread") {
      filtered = filtered.filter((n) => !n.is_read);
    } else if (filter === "read") {
      filtered = filtered.filter((n) => n.is_read);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (n) =>
          (n.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (n.message || n.body || n.content || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNotifications(filtered);
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiCall("put", `/notifications/${notificationId}/mark-read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      toast.success("Marked as read");
    } catch (err) {
      console.error("Error marking as read:", err);
      toast.error("Failed to mark as read");
    }
  };

  const markAsUnread = async (notificationId) => {
    try {
      await apiCall("put", `/notifications/${notificationId}/mark-unread`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: false } : n
        )
      );
      toast.success("Marked as unread");
    } catch (err) {
      console.error("Error marking as unread:", err);
      toast.error("Failed to mark as unread");
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiCall("put", "/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
      toast.success("All notifications marked as read");
    } catch (err) {
      console.error("Error marking all as read:", err);
      toast.error("Failed to mark all as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await apiCall("delete", `/notifications/${notificationId}`);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setSelectedNotifications((prev) => prev.filter((id) => id !== notificationId));
      toast.success("Notification deleted");
    } catch (err) {
      console.error("Error deleting notification:", err);
      toast.error("Failed to delete notification");
    }
  };

  const bulkMarkAsRead = async () => {
    if (selectedNotifications.length === 0) {
      toast.warning("Please select notifications");
      return;
    }

    try {
      await apiCall("put", "/notifications/bulk-mark-read", {
        notification_ids: selectedNotifications,
      });
      setNotifications((prev) =>
        prev.map((n) =>
          selectedNotifications.includes(n.id) ? { ...n, is_read: true } : n
        )
      );
      setSelectedNotifications([]);
      toast.success("Selected notifications marked as read");
    } catch (err) {
      console.error("Error in bulk mark as read:", err);
      toast.error("Failed to mark selected as read");
    }
  };

  const bulkDelete = async () => {
    if (selectedNotifications.length === 0) {
      toast.warning("Please select notifications");
      return;
    }

    try {
      await apiCall("delete", "/notifications/bulk-delete", {
        notification_ids: selectedNotifications,
      });
      setNotifications((prev) => prev.filter((n) => !selectedNotifications.includes(n.id)));
      setSelectedNotifications([]);
      toast.success("Selected notifications deleted");
    } catch (err) {
      console.error("Error in bulk delete:", err);
      toast.error("Failed to delete selected notifications");
    }
  };

  const toggleSelection = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const selectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map((n) => n.id));
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Just now";

    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    }
  };

  const getAvatar = (notification) => {
    if (notification.user_avatar || notification.avatar) {
      return (
        <img
          src={notification.user_avatar || notification.avatar}
          alt=""
          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
        />
      );
    } else if (notification.user_name || notification.name) {
      const name = notification.user_name || notification.name;
      const initials = name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
      return <span style={{ fontSize: '14px', fontWeight: 600 }}>{initials}</span>;
    } else {
      return <Bell size={18} />;
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200 text-green-600";
      case "info":
        return "bg-blue-50 border-blue-200 text-blue-600";
      case "warning":
        return "bg-amber-50 border-amber-200 text-amber-600";
      case "danger":
        return "bg-red-50 border-red-200 text-red-600";
      default:
        return "bg-gray-50 border-gray-200 text-gray-600";
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'var(--bg-color)',
      fontFamily: 'var(--default-font)',
      padding: '24px',
      color: 'var(--text-primary-light)'
    }}>
      <style>{`
        .notification-card {
          background: var(--white);
          border-radius: var(--rounded-16);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .notification-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .notification-header {
          background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-500) 100%);
          padding: 24px;
          color: white;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          border: 1px solid var(--border-color);
          border-radius: var(--rounded-12);
          background: var(--input-bg);
          color: var(--text-primary-light);
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--primary-600);
          box-shadow: 0 0 0 3px rgba(230, 60, 35, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary-light);
          pointer-events: none;
        }

        .filter-btn {
          padding: 10px 20px;
          border: 1px solid var(--border-color);
          border-radius: var(--rounded-12);
          background: var(--white);
          color: var(--text-primary-light);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-btn:hover {
          background: var(--neutral-50);
        }

        .filter-btn.active {
          background: var(--primary-600);
          color: white;
          border-color: var(--primary-600);
        }

        .action-btn {
          padding: 10px 18px;
          border: none;
          border-radius: var(--rounded-12);
          background: var(--primary-600);
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn:hover:not(:disabled) {
          background: var(--primary-700);
          transform: translateY(-1px);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .action-btn.secondary {
          background: white;
          color: var(--primary-600);
          border: 1px solid var(--primary-600);
        }

        .action-btn.secondary:hover:not(:disabled) {
          background: var(--primary-50);
        }

        .action-btn.danger {
          background: var(--danger-600);
        }

        .action-btn.danger:hover:not(:disabled) {
          background: var(--danger-700);
        }

        .notification-item {
          padding: 20px;
          border-bottom: 1px solid var(--border-color);
          transition: all 0.2s ease;
          cursor: pointer;
          position: relative;
        }

        .notification-item:last-child {
          border-bottom: none;
        }

        .notification-item:hover {
          background: var(--neutral-50);
        }

        .notification-item.unread {
          background: linear-gradient(90deg, var(--primary-10) 0%, transparent 100%);
        }

        .notification-item.unread::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: var(--primary-600);
          border-radius: 0 4px 4px 0;
        }

        .avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 2px solid;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: var(--rounded-12);
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .badge.new {
          background: var(--primary-600);
          color: white;
        }

        .icon-btn {
          width: 36px;
          height: 36px;
          border: 1px solid var(--border-color);
          border-radius: var(--rounded-8);
          background: white;
          color: var(--text-secondary-light);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .icon-btn:hover {
          background: var(--neutral-50);
          border-color: var(--primary-600);
          color: var(--primary-600);
        }

        .icon-btn.danger:hover {
          border-color: var(--danger-600);
          color: var(--danger-600);
        }

        .checkbox {
          width: 20px;
          height: 20px;
          border: 2px solid var(--border-color);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .checkbox:checked {
          background: var(--primary-600);
          border-color: var(--primary-600);
        }

        .empty-state {
          padding: 80px 20px;
          text-align: center;
          color: var(--text-secondary-light);
        }

        .stats-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: var(--rounded-12);
          color: white;
          font-size: 14px;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .controls-row {
            flex-direction: column;
          }
          
          .filter-group {
            width: 100%;
            justify-content: stretch !important;
          }

          .filter-btn {
            flex: 1;
            justify-content: center;
          }
        }
      `}</style>

        <Breadcrumb title="All Notifications" />
      

      <div className="notification-card">
        {/* Header */}
        <div className="notification-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 'var(--rounded-16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Bell size={28} />
              </div>
              <div>
                <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>
                  Notifications
                </h1>
                <p style={{ fontSize: '14px', opacity: 0.9, margin: 0 }}>
                  Stay updated with your latest activities
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <div className="stats-badge">
                <span style={{ 
                  width: '8px', 
                  height: '8px', 
                  background: 'white',
                  borderRadius: '50%'
                }} />
                {unreadCount} Unread
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-color)', background: 'var(--white)' }}>
          <div className="controls-row" style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div className="search-box">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                className="search-input"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-group" style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button
                className={`filter-btn ${filter === "all" ? "active" : ""}`}
                onClick={() => setFilter("all")}
              >
                All <span style={{ opacity: 0.7 }}>({notifications.length})</span>
              </button>
              <button
                className={`filter-btn ${filter === "unread" ? "active" : ""}`}
                onClick={() => setFilter("unread")}
              >
                <Mail size={16} />
                Unread <span style={{ opacity: 0.7 }}>({unreadCount})</span>
              </button>
              <button
                className={`filter-btn ${filter === "read" ? "active" : ""}`}
                onClick={() => setFilter("read")}
              >
                <MailOpen size={16} />
                Read <span style={{ opacity: 0.7 }}>({notifications.length - unreadCount})</span>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                className="checkbox"
                checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
                onChange={selectAll}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary-light)' }}>
                {selectedNotifications.length > 0 
                  ? `${selectedNotifications.length} selected`
                  : 'Select all'
                }
              </span>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {selectedNotifications.length > 0 ? (
                <>
                  <button className="action-btn" onClick={bulkMarkAsRead}>
                    <Check size={16} />
                    Mark Read
                  </button>
                  <button className="action-btn danger" onClick={bulkDelete}>
                    <Trash2 size={16} />
                    Delete
                  </button>
                </>
              ) : (
                <button 
                  className="action-btn secondary" 
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                >
                  <CheckCircle size={16} />
                  Mark All Read
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px' }}>
              <LoadingComponent />
            </div>
          ) : error ? (
            <div className="empty-state">
              <p style={{ color: 'var(--danger-600)', fontSize: '16px' }}>{error}</p>
            </div>
          ) : filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification, index) => (
              <div
                key={notification.id || index}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
              >
                <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedNotifications.includes(notification.id)}
                    onChange={() => toggleSelection(notification.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  <div className={`avatar ${getTypeStyles(notification.type)}`}>
                    {getAvatar(notification)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <h6 style={{ 
                            fontSize: '15px', 
                            fontWeight: 600, 
                            margin: 0,
                            color: 'var(--text-primary-light)'
                          }}>
                            {notification.title || notification.subject || "Notification"}
                          </h6>
                          {!notification.is_read && (
                            <span className="badge new">New</span>
                          )}
                        </div>
                        <p style={{ 
                          fontSize: '14px', 
                          color: 'var(--text-secondary-light)',
                          margin: '0 0 8px 0',
                          lineHeight: 1.5
                        }}>
                          {notification.message || notification.body || notification.content}
                        </p>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary-light)',
                          fontWeight: 500
                        }}>
                          {formatTime(notification.created_at || notification.timestamp)}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {notification.is_read ? (
                          <button
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsUnread(notification.id);
                            }}
                            title="Mark as unread"
                          >
                            <Mail size={16} />
                          </button>
                        ) : (
                          <button
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            title="Mark as read"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        <button
                          className="icon-btn danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          title="Delete"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <Bell size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary-light)' }}>
                No notifications found
              </h3>
              <p style={{ fontSize: '14px', margin: 0 }}>
                {searchTerm
                  ? "Try adjusting your search"
                  : filter === "unread"
                  ? "You're all caught up!"
                  : filter === "read"
                  ? "No read notifications"
                  : "No notifications to display"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;